// Converted to typescript through help of ChatGPT

import type { Map as ImmutableMap } from 'immutable';
import { createCachedSelector, LruObjectCache } from 're-reselect';

import { registerCachedSelectors } from 'app-components/utils/core/reselect/selectorCacheRegistry';
import type RootState from 'app-components/utils/redux-store/rootState';

import { selectorsHelpers } from '../core';
import { DOCUMENTS_REDUCER_KEY, type Document, type DocumentProps } from './documentsTypes';

type State = Pick<RootState, typeof DOCUMENTS_REDUCER_KEY>;

// Selectors
export const getDocumentsStore = (state: State) => state[DOCUMENTS_REDUCER_KEY];
export const getLastSyncDate = (state: State) => getDocumentsStore(state).lastSyncDate;

export const getDocuments = (state: State): ImmutableMap<string, Document> => {
  const newState = getDocumentsStore(state);
  return newState.resourcesById;
};

export const getDocumentsForIds = createCachedSelector(
  [getDocuments, (state: State, ids: string[]) => ids],
  (documentsMap, ids) =>
    Array.from(documentsMap.values()).filter(
      (document: DocumentProps) => document.id && ids.includes(document.id as string),
    ),
)({
  keySelector: (state: State, ids: string[]) => ids?.join(',') || 'none',
  cacheObject: new LruObjectCache({ cacheSize: 5 }),
});

/** A transaction attachment, matched to its document by id or, while uploading, by client id. */
type AttachmentRef = { id?: string | null; clientId?: string | null };

export const getDocumentsForAttachments = createCachedSelector(
  getDocuments,
  (state: State, attachments: readonly AttachmentRef[] | null | undefined) => attachments,
  selectorsHelpers.createValueCache(
    (documentsMap: ImmutableMap<string, Document>, attachments: readonly AttachmentRef[] | null | undefined) =>
      Array.from(documentsMap.values()).filter((document: Document) =>
        attachments?.find((attachment) => attachment.id === document.id || attachment.clientId === document.clientId),
      ),
  ),
)({
  keySelector: (state: State, attachments: readonly AttachmentRef[] | null | undefined) =>
    attachments?.map((attachment) => attachment.id || attachment.clientId).join(',') || 'none',
  cacheObject: new LruObjectCache({ cacheSize: 5 }),
});

// A store reset drops every key's selector (see selectorCacheRegistry).
registerCachedSelectors(getDocumentsForIds, getDocumentsForAttachments);
